import { SearchInput } from "@/components/flexkit/SearchInput";
import type { TableSortOrder } from "@/components/flexkit/tables/SortableTable";
import { toApiSortOrder } from "@/components/flexkit/tables/sorting-utils";
import {
    MembersList,
    type MembersListPaginationProps,
    type SortColumn,
} from "@/components/settings/organization/members/MembersList";
import { MemberRoleFilter } from "@/components/settings/organization/members/MemberRoleFilter";
import { MemberStatusFilter } from "@/components/settings/organization/members/MemberStatusFilter";
import {
    RoleFilterValue,
    roleFilterValueToApiRoles,
    StatusFilterValue,
    statusFilterValueToApiStatuses,
} from "@/components/settings/organization/members/members-utils";
import { Flags } from "@/feature-flags";
import { useFlag } from "@/hooks/use-flag";
import { useMembership } from "@/hooks/use-membership";
import { useMembers, usePaginatedMembers } from "@/queries/organization-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import type { ListMembersRequest_SortField } from "gitpod-next-api/gitpod/v1/organization_pb";
import { parseAsInteger, useQueryState } from "nuqs";
import { useCallback, useEffect, useState, type FC } from "react";
import { useDebounce } from "use-debounce";

export const MembersTab: FC = () => {
    const { value: isPaginatedEnabled, loading: flagLoading } = useFlag(Flags.PaginatedMembersList, false);

    const { data: user } = useAuthenticatedUser();
    const { membership } = useMembership();
    const [searchQuery, setSearchQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState<RoleFilterValue>(RoleFilterValue.All);
    const [statusFilter, setStatusFilter] = useState<StatusFilterValue>(StatusFilterValue.All);

    const [sortColumn, setSortColumn] = useState<SortColumn | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>(undefined);

    // Page-based pagination state (1-based in URL, converted to 0-based for API)
    const [currentPage, setCurrentPage] = useQueryState("page", parseAsInteger.withDefault(1));

    const [debouncedSearchQuery] = useDebounce(searchQuery, 250, { trailing: true });

    const apiRoles = roleFilterValueToApiRoles(roleFilter);
    const apiStatuses = statusFilterValueToApiStatuses(statusFilter);

    const filterSort = {
        filter: {
            search: debouncedSearchQuery,
            roles: apiRoles,
            statuses: apiStatuses,
        },
        sort: {
            field: sortColumn,
            order: toApiSortOrder(sortOrder),
        },
    };

    // Use paginated hook when flag is enabled
    const paginatedQuery = usePaginatedMembers({
        ...filterSort,
        page: currentPage - 1,
        enabled: !flagLoading && isPaginatedEnabled,
    });

    // Use auto-paginate hook when flag is disabled
    const allMembersQuery = useMembers({
        ...filterSort,
        enabled: !flagLoading && !isPaginatedEnabled,
    });

    // Select the appropriate data based on flag
    const members = isPaginatedEnabled ? paginatedQuery.data : (allMembersQuery.data?.members ?? []);
    const isPending = isPaginatedEnabled ? paginatedQuery.isPending : allMembersQuery.isPending;
    const isFetching = isPaginatedEnabled ? paginatedQuery.isFetching : allMembersQuery.isFetching;

    // Reset page if it's invalid
    useEffect(() => {
        if (currentPage < 1) {
            void setCurrentPage(1);
        }
    }, [currentPage, setCurrentPage]);

    // Reset page if no members on current page
    useEffect(() => {
        if (members.length === 0 && !isPending) {
            void setCurrentPage(1);
        }
    }, [members.length, isPending, setCurrentPage]);

    // Reset page when search or filters change
    useEffect(() => {
        if (debouncedSearchQuery || roleFilter !== RoleFilterValue.All || statusFilter !== StatusFilterValue.All) {
            void setCurrentPage(1);
        }
    }, [debouncedSearchQuery, roleFilter, statusFilter, setCurrentPage]);

    const handleSort = useCallback((columnName: ListMembersRequest_SortField, order: TableSortOrder) => {
        setSortColumn(columnName);
        setSortOrder(order);
    }, []);

    const handleResetFilters = useCallback(() => {
        setSearchQuery("");
        setRoleFilter(RoleFilterValue.All);
        setStatusFilter(StatusFilterValue.All);
    }, []);

    const handlePageChange = useCallback(
        (page: number) => {
            void setCurrentPage(page + 1);
        },
        [setCurrentPage],
    );

    // Build pagination props only when flag is enabled
    const pagination: MembersListPaginationProps | undefined = isPaginatedEnabled
        ? {
              currentPage: currentPage - 1,
              totalPages: paginatedQuery.totalPages,
              totalCount: paginatedQuery.totalCount,
              pageSize: paginatedQuery.pageSize,
              onPageChange: handlePageChange,
          }
        : undefined;

    return (
        <>
            <div className="flex flex-col gap-2 @container @lg:flex-row @lg:items-center">
                <SearchInput
                    wrapperClassName="flex-1"
                    className="max-w-none @sm:min-w-[20ch]"
                    placeholder="Search by name, email or ID"
                    value={searchQuery || ""}
                    onChange={(e) => setSearchQuery(e.target.value.trimStart().replace(/\s+/g, " "))}
                    onClear={() => setSearchQuery("")}
                    isLoading={isFetching}
                    data-tracking-id="search-members-input"
                />
                <div className="flex flex-col items-stretch gap-2 @sm:flex-row">
                    <MemberRoleFilter value={roleFilter} onChange={setRoleFilter} />
                    <MemberStatusFilter
                        value={statusFilter}
                        onChange={setStatusFilter}
                        showCount={roleFilter === RoleFilterValue.All && debouncedSearchQuery.trim().length === 0}
                    />
                </div>
            </div>
            <MembersList
                user={user}
                membership={membership}
                data={{
                    members: members,
                }}
                isLoading={isPending}
                isFetching={isFetching}
                hasSearchQuery={debouncedSearchQuery.trim().length > 0}
                roleFilter={roleFilter}
                statusFilter={statusFilter}
                onResetFilters={handleResetFilters}
                setSort={handleSort}
                sort={{
                    sortBy: sortColumn,
                    sortOrder,
                }}
                pagination={pagination}
            />
        </>
    );
};
